<template>
  <div
    class="task-mobile-card"
    :class="{
      active: active,
      done: !!task.completed_at || Number(task.archived) === 1,
    }"
    @click="$emit('select', task.id)"
  >
    <div class="tm-title-row">
      <button
        type="button"
        class="task-complete-btn"
        :class="{ completed: !!task.completed_at }"
        :title="task.completed_at ? t('tasks-reopen') : t('tasks-complete')"
        :disabled="saving"
        @click.stop="$emit('toggle-complete', task)"
      >
        <Icon icon-class="ui-check" size="2" />
      </button>
      <span class="tm-title">{{ task.title }}</span>
      <div class="tm-tags" @click.stop>
        <answer-tags
          compact
          display="dots"
          :tags="task.tags || []"
          :suggestions="placeTags"
          :busy="tagBusy"
          @apply="$emit('apply-tag', { task, payload: $event })"
          @remove="$emit('remove-tag', { task, tag: $event })"
          @update="$emit('update-tag', $event)"
          @delete="$emit('delete-tag', $event)"
        />
      </div>
      <kv-chip
        v-if="task.completed_at"
        size="small"
        density="extra-compact"
        rounded="pill"
        color="success"
      >
        {{ t("tasks-status_completed") }}
      </kv-chip>
      <kv-chip
        v-else-if="Number(task.archived) === 1"
        size="small"
        density="extra-compact"
        rounded="pill"
        color="muted"
      >
        {{ t("tasks-status_archived") }}
      </kv-chip>
    </div>

    <kv-popup
      class="task-client-popup"
      placement="bottom right"
      min-width="280px"
      max-width="calc(100vw - 32px)"
    >
      <template #trigger="{ toggle }">
        <button
          type="button"
          class="tm-client-btn"
          :class="{ empty: !clientLabel }"
          :title="clientLabel || t('tasks-col_client')"
          :disabled="saving"
          @click.stop="toggle"
        >
          <Icon icon-class="ui-user-01" size="2" />
          <span>{{ clientLabel || t("tasks-col_client") }}</span>
        </button>
      </template>
      <template #content="{ toggle }">
        <div class="task-client-menu">
          <div class="task-client-field" @click.stop>
            <clients-field
              :value="task.clients_id || ''"
              @input="$emit('set-client', { task, value: $event, toggle })"
            />
          </div>
          <button
            v-if="task.clients_id"
            type="button"
            class="task-hot-item"
            @click.stop.prevent="
              $emit('set-client', { task, value: null, toggle })
            "
          >
            {{ t("tasks-linked_answer_none") }}
          </button>
        </div>
      </template>
    </kv-popup>

    <div class="tm-meta">
      <div class="tm-meta-row">
        <kv-popup placement="bottom right" min-width="180px">
          <template #trigger="{ toggle }">
            <button
              type="button"
              class="task-flag-btn"
              :class="'tone-' + priorityTone"
              :title="priorityLabel"
              :aria-label="priorityLabel"
              :disabled="saving"
              @click.stop="toggle"
            >
              <Icon icon-class="ui-flag-01" size="2" />
            </button>
          </template>
          <template #content="{ toggle }">
            <div class="task-hot-menu">
              <button
                v-for="opt in priorityOptions"
                :key="opt.value"
                type="button"
                class="task-hot-item"
                :class="'tone-' + opt.tone"
                @click.stop.prevent="
                  $emit('set-priority', { task, value: opt.value, toggle })
                "
              >
                <Icon
                  :icon-class="opt.value === 0 ? 'ui-x-close' : 'ui-flag-01'"
                  size="3"
                />
                <span>{{ opt.label }}</span>
              </button>
            </div>
          </template>
        </kv-popup>

        <kv-popup placement="bottom right" min-width="220px" max-width="280px">
          <template #trigger="{ toggle }">
            <button
              type="button"
              class="task-avatar-btn"
              :title="task.assignee_name || t('tasks-col_assignee')"
              :disabled="saving"
              @click.stop="toggle"
            >
              <span v-if="task.assignee_name" class="task-avatar">
                {{ initials(task.assignee_name) }}
              </span>
              <span v-else class="task-avatar empty">
                <Icon icon-class="ui-user-01" size="2" />
              </span>
            </button>
          </template>
          <template #content="{ toggle }">
            <div class="task-hot-menu">
              <button
                type="button"
                class="task-hot-item"
                @click.stop.prevent="
                  $emit('set-assignee', { task, user: null, toggle })
                "
              >
                {{ t("tasks-none") }}
              </button>
              <button
                v-for="user in canAssignTo"
                :key="user.id"
                type="button"
                class="task-hot-item"
                :class="{
                  active: String(user.id) === String(task.assignee_id),
                }"
                @click.stop.prevent="
                  $emit('set-assignee', { task, user, toggle })
                "
              >
                <span class="task-avatar mini">{{ initials(user.name) }}</span>
                <span>{{ user.name }}</span>
              </button>
            </div>
          </template>
        </kv-popup>

        <div class="tm-due" @click.stop>
          <kv-date-picker
            :value="dueDateValue || null"
            value-format="yyyy-MM-dd"
            size="small"
            variant="tonal"
            placement="bottom right"
            :placeholder="t('tasks-no_date')"
            :disabled="saving"
            :clearable="true"
            @input="$emit('set-due', { task, value: $event || null })"
          >
            <template #trigger>
              <button
                type="button"
                class="task-due-pill"
                :class="{ empty: !dueDateValue, late: dueTone === 'late' }"
                :disabled="saving"
                :title="t('tasks-col_due_date')"
              >
                <Icon icon-class="ui-calendar" size="2" />
                <human-date
                  v-if="dueDateValue"
                  :date="dueDateValue + 'T12:00:00'"
                  :format="t('dates-format_weekday_date')"
                />
                <span v-else>{{ t("tasks-no_date") }}</span>
              </button>
            </template>
          </kv-date-picker>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from "../components/icon/icon";
import KvChip from "../components/ui/kv-chip.vue";
import KvPopup from "../components/ui/kv-popup.vue";
import KvDatePicker from "../components/inputs/kv-date-picker.vue";
import HumanDate from "../components/human-date/human-date";
import ClientsField from "../clients/clients-field";
import AnswerTags from "../inbox/answer-tags.vue";
import {
  taskAssigneeInitials,
  taskClientName,
  taskPriorityFlagTone,
} from "../helpers/taskDisplay";

export default {
  name: "task-mobile-card",
  components: {
    Icon,
    HumanDate,
    "kv-chip": KvChip,
    "kv-popup": KvPopup,
    "kv-date-picker": KvDatePicker,
    "clients-field": ClientsField,
    "answer-tags": AnswerTags,
  },
  props: {
    task: { type: Object, required: true },
    canAssignTo: { type: Array, default: () => [] },
    priorityOptions: { type: Array, default: () => [] },
    placeTags: { type: Array, default: () => [] },
    tagBusy: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
  },
  computed: {
    clientLabel() {
      return taskClientName(this.task) || "";
    },
    priorityTone() {
      return taskPriorityFlagTone(this.task.priority);
    },
    priorityLabel() {
      const opt = this.priorityOptions.find(
        (o) => Number(o.value) === Number(this.task.priority || 0),
      );
      return (opt && opt.label) || this.t("tasks-col_priority");
    },
    dueDateValue() {
      if (!this.task.due_date) return "";
      return String(this.task.due_date).slice(0, 10);
    },
    dueTone() {
      if (!this.task.due_date || this.task.completed_at) return "";
      const due = new Date(
        String(this.task.due_date).slice(0, 10) + "T12:00:00",
      );
      if (Number.isNaN(due.getTime())) return "";
      const today = new Date();
      today.setHours(12, 0, 0, 0);
      const diff = (due.getTime() - today.getTime()) / (24 * 60 * 60 * 1000);
      if (diff < 0) return "late";
      if (diff < 1) return "today";
      return "";
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    initials(name) {
      return taskAssigneeInitials(name);
    },
  },
};
</script>

<style scoped lang="scss">
.task-mobile-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: #fff;
  border: 1px solid #f3f4f6;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;

  &.active {
    border-color: #a6f4c5;
    box-shadow: 0 0 0 1px #edfcf3;
  }

  &.done {
    opacity: 0.65;

    .tm-title {
      color: #9da4ae;
      text-decoration: line-through;
    }
  }
}

.tm-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.tm-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 15px;
  font-weight: 500;
  color: #111927;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.tm-tags {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

::v-deep .task-client-popup.kv-popup-container {
  display: block;
  width: 100%;
  align-self: stretch;

  > div:first-child {
    display: block;
    width: 100%;
  }
}

.tm-client-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  width: auto;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: #384250;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  justify-content: flex-start;

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.empty {
    color: #9da4ae;
    font-weight: 400;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.tm-meta {
  display: flex;
  flex-direction: column;
  padding-top: 10px;
  border-top: 1px solid #f3f4f6;
}

.tm-meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.tm-due {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 220px;

  ::v-deep .kv-date-picker,
  ::v-deep .kv-date-picker-trigger {
    display: inline-flex;
    width: auto;
    max-width: 100%;
  }
}

.task-due-pill {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: #f3f4f6;
  color: #384250;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  max-width: 100%;

  ::v-deep .human-date {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.empty {
    color: #9da4ae;
    background: #fff;
    border-color: #e5e7eb;
    border-style: dashed;
  }

  &.late {
    color: #f04438;
    background: #fef3f2;
    border-color: transparent;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.task-avatar-btn {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.task-avatar {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef2ff;
  color: #3538cd;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;

  &.empty {
    background: #f3f4f6;
    color: #9da4ae;
  }

  &.mini {
    width: 22px;
    height: 22px;
    font-size: 10px;
    flex-shrink: 0;
  }
}

.task-flag-btn {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;

  &.tone-high {
    color: #f04438;
    background: #fef3f2;
    border-color: transparent;
  }

  &.tone-medium {
    color: #dc6803;
    background: #fffaeb;
    border-color: transparent;
  }

  &.tone-low {
    color: #384250;
    background: #f3f4f6;
    border-color: transparent;
  }

  &.tone-undefined {
    color: #9da4ae;
    border-style: dashed;
    background: transparent;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.task-complete-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  margin: 0;
  border-radius: 999px;
  border: 1.5px dashed #d2d6db;
  background: transparent;
  color: #d2d6db;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;

  &.completed {
    color: var(--primary, #0b9055);
    border-style: solid;
    border-color: #a6f4c5;
    background: #edfcf3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.task-hot-menu {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 280px;
  overflow: auto;
  min-width: 160px;
}

.task-hot-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: transparent;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  text-align: left;
  color: #384250;
  cursor: pointer;

  &:hover {
    background: #f9fafb;
  }

  &.active {
    background: #edfcf3;
    color: #0b9055;
  }

  &.disabled {
    cursor: default;
    color: #9da4ae;
  }

  &.tone-high {
    color: #f04438;
  }
  &.tone-medium {
    color: #dc6803;
  }
  &.tone-low {
    color: #384250;
  }
  &.tone-undefined {
    color: #6c737f;
  }
}

::v-deep .task-client-popup.kv-popup-container .kv-popup-content {
  z-index: 3000;
  background: #fff;
  overflow: visible;
  max-width: calc(100vw - 24px);
}

.task-client-menu {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fff;
}

.task-client-field {
  display: block;
  width: 100%;
  background: #fff;

  ::v-deep .autocomplete {
    width: 100%;
    background: #fff;
  }

  ::v-deep .autocomplete-input {
    width: 100%;
    min-height: 44px;
    font-size: 14px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fff;
  }

  ::v-deep .autocomplete-result-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    width: 100%;
    min-height: 120px;
    max-height: 260px;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fff !important;
    box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);
    z-index: 4000 !important;

    li {
      margin: 0;
      padding: 12px 14px;
      font-size: 14px;
      line-height: 1.45;
      color: #111927;
      background: #fff;
      border-bottom: 1px solid #f3f4f6;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        background: #f9fafb;
      }
    }
  }
}
</style>
